import { backendService } from "src/core/backend/services/backendService";
import { useGtm } from "src/core/hooks/useGtm";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  LoginRequest,
  RegisterRequest,
  TokenResponse,
  UpdatePasswordRequest,
  UserResponse,
} from "../../../core/backend/clients/backendClient";
import { logService } from "../../../core/services/logService";
import { User } from "../models/userModel";
import { authService } from "./authService";

interface AuthState {
  readonly loading: boolean;
  readonly loadingForgotPassword: boolean;
  readonly token: string;
  readonly user: User;

  clear: () => void;

  login: (request: LoginRequest) => Promise<User>;
  loginByToken: (email: string, token: string) => Promise<void>;
  logout: () => void;
  register: (request: RegisterRequest) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  activate: (key: string) => Promise<void>;
  resetPassword: (request: UpdatePasswordRequest) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      loading: false,
      loadingForgotPassword: false,
      token: "",
      user: new User(),

      clear: () => {
        set({
          loading: false,
          loadingForgotPassword: false,
          token: "",
          user: new User(),
        });
      },

      login: async (request: LoginRequest) => {
        set({ loading: true });

        const gtm = useGtm();

        let user: User = new User();

        await authService
          .login(request)
          .then((response: TokenResponse) => {
            if (logService.globalLog)
              logService.info("login", request.email ?? "");
            authService.mapUser(user, request.email ?? "", response);
          })
          .catch((error) => {
            logService.error("login", error);
            set({ loading: false });
            throw error;
          });

        set({ token: user.token ?? "" });

        if (!!user?.token)
          await authService
            .getUserInfo()
            .then((response: UserResponse) => {
              backendService.personResponseFix(response);
              if (logService.globalLog)
                logService.info("login-getUserInfo response", response);
              authService.setUserInfo(user, response);
            })
            .catch((error) => {
              logService.error("getUserInfo", error);
              set({ loading: false });
              throw error;
            });

        set({ user, loading: false });

        gtm.event("login", { user: user.name });

        return user;
      },

      loginByToken: async (email: string, token: string) => {
        const user = authService.loginByToken(email, token);

        if (!!user?.token)
          await authService
            .getUserInfo()
            .then((response: UserResponse) => {
              backendService.personResponseFix(response);
              if (logService.globalLog)
                logService.info("loginByToken-getUserInfo response", response);
              authService.setUserInfo(user, response);
            })
            .catch((error) => {
              logService.error("getUserInfo", error);
              set({ loading: false });
              throw error;
            });

        set({ user, loading: false });
      },

      logout: () => get().clear(),

      register: async (request: RegisterRequest) => {
        set({ loading: true });

        backendService.nullToUndefined(request);
        backendService.personRequestFix(request);

        return await authService
          .register(request)
          .then(() => {
            if (logService.globalLog) logService.info("register", request);
            set({ loading: false });
          })
          .catch((error) => {
            logService.error("register", error);
            set({ loading: false });
            throw error;
          });
      },

      forgotPassword: async (email: string) => {
        set({ loadingForgotPassword: true });

        await authService
          .forgottenPassword(email)
          .then(() => {
            if (logService.globalLog)
              logService.info("forgottenPassword", email);
          })
          .catch((error) => {
            logService.error("forgotPassword", error);
            set({ loadingForgotPassword: false });
            throw error;
          });

        set({ loadingForgotPassword: false });
      },

      activate: async (key: string) => {
        set({ loading: true });

        await authService
          .activate(key)
          .then(() => {
            if (logService.globalLog) logService.info("activate", key);
          })
          .catch((error) => {
            logService.error("activate", error);
            set({ loading: false });
            throw error;
          });

        set({ loading: false });
      },

      resetPassword: async (request: UpdatePasswordRequest) => {
        set({ loading: true });

        await authService
          .resetPassword(request)
          .then(() => {
            if (logService.globalLog) logService.info("resetPassword");
          })
          .catch((error) => {
            logService.error("resetPassword", error);
            set({ loading: false });
            throw error;
          });

        set({ loading: false });
      },
    }),
    {
      name: "authStore",
      merge: (persistedState, currentState) => {
        const merged = { ...currentState, ...(persistedState as any) };
        if (merged.user) {
          if (
            merged.user.birthDate &&
            typeof merged.user.birthDate === "string"
          ) {
            merged.user.birthDate = new Date(merged.user.birthDate);
          }
          if (
            merged.user.expiresAt &&
            typeof merged.user.expiresAt === "string"
          ) {
            merged.user.expiresAt = new Date(merged.user.expiresAt);
          }
        }
        return merged;
      },
    }
  )
);
